/*!
 * Ferial Rose — PDF Sign
 * A free, private, in-browser PDF signing tool.
 * Documents and signatures are processed entirely in the visitor's browser.
 * Nothing is uploaded, stored or sent anywhere.
 *
 * Libraries (loaded on demand, self-hosted as theme assets):
 *   - PDF.js (Mozilla, Apache-2.0) for rendering pages
 *   - pdf-lib (MIT) for embedding signatures into the original PDF
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-frps]');
  if (!root || root.dataset.frpsReady) return;
  root.dataset.frpsReady = '1';

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */
  const abs = (u) => new URL(u, document.baseURI).href;
  const cfg = {
    pdfjs: abs(root.dataset.pdfjsSrc),
    worker: abs(root.dataset.workerSrc),
    pdflib: abs(root.dataset.pdflibSrc),
    maxMB: Math.max(1, Number(root.dataset.maxMb) || 25),
    maxPages: 500,
    maxSigImageMB: 5,
  };

  const SIG_FONTS = [
    { family: 'Dancing Script', weight: 600, label: 'Classic' },
    { family: 'Great Vibes', weight: 400, label: 'Elegant' },
    { family: 'Caveat', weight: 600, label: 'Casual' },
    { family: 'Homemade Apple', weight: 400, label: 'Handwritten' },
  ];
  const FONT_CSS =
    'https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=Dancing+Script:wght@600&family=Great+Vibes&family=Homemade+Apple&display=swap';

  const INK = { black: '#111111', blue: '#1b3a8c', red: '#a11d2a' };

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */
  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const uid = () => Math.random().toString(36).slice(2, 10);
  const dpr = () => Math.min(window.devicePixelRatio || 1, 3);

  function h(tag, attrs = {}, ...kids) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const kid of kids.flat()) if (kid != null) el.append(kid);
    return el;
  }

  const ICONS = {
    trash: '<path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/>',
    swap: '<path d="M4 8h13l-3-3M20 16H7l3 3"/>',
    copy: '<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/>',
    resize: '<path d="M20 14v6h-6M20 20l-7-7"/>',
    pencil: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>',
  };
  const icon = (name) =>
    `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;

  /* ------------------------------------------------------------------ */
  /* Library loading (on demand)                                         */
  /* ------------------------------------------------------------------ */
  let pdfjsPromise = null;
  let pdflibPromise = null;

  function loadPdfjs() {
    if (!pdfjsPromise) {
      pdfjsPromise = import(cfg.pdfjs).then((lib) => {
        lib.GlobalWorkerOptions.workerSrc = cfg.worker;
        return lib;
      });
      pdfjsPromise.catch(() => (pdfjsPromise = null));
    }
    return pdfjsPromise;
  }

  function loadPdfLib() {
    if (!pdflibPromise) {
      pdflibPromise = new Promise((resolve, reject) => {
        if (window.PDFLib) return resolve(window.PDFLib);
        const s = document.createElement('script');
        s.src = cfg.pdflib;
        s.async = true;
        s.onload = () => (window.PDFLib ? resolve(window.PDFLib) : reject(new Error('pdf-lib missing')));
        s.onerror = () => reject(new Error('pdf-lib failed to load'));
        document.head.appendChild(s);
      });
      pdflibPromise.catch(() => (pdflibPromise = null));
    }
    return pdflibPromise;
  }

  function warmUp() {
    loadPdfjs().catch(() => {});
    loadPdfLib().catch(() => {});
  }

  let fontsPromise = null;
  function loadSignatureFonts() {
    if (!fontsPromise) {
      fontsPromise = new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = FONT_CSS;
        link.onload = () => {
          Promise.all(SIG_FONTS.map((f) => document.fonts.load(`${f.weight} 48px "${f.family}"`).catch(() => null)))
            .then(() => resolve(true));
        };
        link.onerror = () => resolve(false);
        document.head.appendChild(link);
        setTimeout(() => resolve(false), 6000);
      });
    }
    return fontsPromise;
  }

  /* ------------------------------------------------------------------ */
  /* DOM references                                                      */
  /* ------------------------------------------------------------------ */
  const el = {
    upload: $('[data-frps-upload]'),
    drop: $('[data-frps-drop]'),
    fileInput: $('[data-frps-file]'),
    error: $('[data-frps-error]'),
    loading: $('[data-frps-loading]'),
    workspace: $('[data-frps-workspace]'),
    fileName: $('[data-frps-filename]'),
    viewer: $('[data-frps-viewer]'),
    pages: $('[data-frps-pages]'),
    pageInput: $('[data-frps-page-input]'),
    pageTotal: $('[data-frps-page-total]'),
    prev: $('[data-frps-prev]'),
    next: $('[data-frps-next]'),
    zoomIn: $('[data-frps-zoom-in]'),
    zoomOut: $('[data-frps-zoom-out]'),
    zoomLabel: $('[data-frps-zoom-label]'),
    undo: $('[data-frps-undo]'),
    redo: $('[data-frps-redo]'),
    addSig: $$('[data-frps-add]'),
    download: $$('[data-frps-download]'),
    restart: $('[data-frps-restart]'),
    tray: $('[data-frps-tray]'),
    trayEmpty: $('[data-frps-tray-empty]'),
    placedCount: $('[data-frps-placed-count]'),
    status: $('[data-frps-status]'),
    dialog: $('[data-frps-dialog]'),
  };

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */
  const state = {
    file: null,
    bytes: null,
    pdf: null,
    pages: [],
    items: [],
    sigs: new Map(),
    selected: null,
    undo: [],
    redo: [],
    zoom: 1,
    current: 0,
    dirty: false,
    observer: null,
  };

  /* ------------------------------------------------------------------ */
  /* Status + errors                                                     */
  /* ------------------------------------------------------------------ */
  let statusTimer;
  function announce(msg, tone = 'info') {
    el.status.textContent = msg;
    el.status.dataset.tone = tone;
    el.status.hidden = false;
    clearTimeout(statusTimer);
    statusTimer = setTimeout(() => (el.status.hidden = true), tone === 'error' ? 7000 : 3500);
  }

  function showError(title, detail) {
    el.error.innerHTML = '';
    el.error.append(h('strong', { text: title }), h('span', { text: detail }));
    el.error.hidden = false;
    el.error.focus({ preventScroll: true });
  }
  function clearError() {
    el.error.hidden = true;
    el.error.innerHTML = '';
  }

  function setLoading(on, text) {
    el.loading.hidden = !on;
    if (text) $('[data-frps-loading-text]').textContent = text;
    el.drop.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------ */
  /* File intake + validation                                            */
  /* ------------------------------------------------------------------ */
  async function handleFile(file) {
    clearError();
    if (!file) return;

    const looksPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
    if (!looksPdf) {
      return showError('That file isn’t a PDF.', 'Please choose a file that ends in .pdf. Word documents and images need to be saved as PDF first.');
    }
    if (file.size === 0) {
      return showError('This file is empty.', 'The PDF has no content. Please check the file and try again.');
    }
    if (file.size > cfg.maxMB * 1024 * 1024) {
      return showError(
        'This PDF is too large.',
        `The limit is ${cfg.maxMB} MB and your file is ${(file.size / 1048576).toFixed(1)} MB. Try compressing the PDF or splitting it into smaller files.`
      );
    }

    setLoading(true, 'Opening your PDF…');
    let bytes;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      setLoading(false);
      return showError('We couldn’t read this file.', 'Your browser was unable to open it. Please try again or choose a different file.');
    }

    // Magic-number check: real PDFs start with "%PDF-" (allow a little junk before it).
    const head = new TextDecoder('latin1').decode(bytes.subarray(0, 1024));
    if (!head.includes('%PDF-')) {
      setLoading(false);
      return showError('This doesn’t look like a valid PDF.', 'The file may be damaged or saved in a different format with a .pdf name.');
    }

    let pdfjs, PDFLib;
    try {
      [pdfjs, PDFLib] = await Promise.all([loadPdfjs(), loadPdfLib()]);
    } catch {
      setLoading(false);
      return showError('The signing tool couldn’t start.', 'Please check your internet connection and refresh the page.');
    }

    // 1) Can we edit it? (encrypted / damaged check with pdf-lib)
    try {
      await PDFLib.PDFDocument.load(bytes, { updateMetadata: false });
    } catch (err) {
      setLoading(false);
      const name = err && (err.name || err.constructor?.name);
      if (name === 'EncryptedPDFError' || /encrypt/i.test(String(err && err.message))) {
        return showError(
          'This PDF is password-protected or locked.',
          'Protected PDFs can’t be signed here. Open it in your PDF app, remove the password or editing restrictions, save a copy, and upload that copy.'
        );
      }
      return showError('This PDF appears to be damaged.', 'We couldn’t read its structure. Try re-saving or re-exporting it as a PDF, then upload it again.');
    }

    // 2) Can we display it? (pdf.js)
    let pdf;
    try {
      const task = pdfjs.getDocument({
        data: bytes.slice(),
        isEvalSupported: false,
        enableXfa: false,
        disableAutoFetch: true,
      });
      pdf = await task.promise;
    } catch (err) {
      setLoading(false);
      if (err && err.name === 'PasswordException') {
        return showError(
          'This PDF is password-protected.',
          'Remove the password in your PDF app, save a copy, and upload that copy.'
        );
      }
      return showError('This PDF appears to be damaged.', 'We couldn’t display it. Try re-saving or re-exporting it as a PDF, then upload it again.');
    }

    if (pdf.numPages > cfg.maxPages) {
      setLoading(false);
      destroyPdf(pdf);
      return showError('This PDF has too many pages.', `Please upload a document with ${cfg.maxPages} pages or fewer.`);
    }

    state.file = file;
    state.bytes = bytes;
    state.pdf = pdf;
    try {
      await buildViewer();
    } catch {
      setLoading(false);
      resetAll(true);
      return showError('This PDF appears to be damaged.', 'Some pages couldn’t be read. Try re-saving it as a PDF and upload it again.');
    }
    setLoading(false);
    el.upload.hidden = true;
    el.workspace.hidden = false;
    el.fileName.textContent = file.name;
    el.fileName.title = file.name;
    updateUI();
    renderTray();
    el.viewer.focus({ preventScroll: true });
    $('.frps-tool').scrollIntoView({ behavior: 'smooth', block: 'start' });
    announce(`Opened ${file.name} — ${pdf.numPages} page${pdf.numPages > 1 ? 's' : ''}. Click “Add signature” to begin.`);
  }

  /* ------------------------------------------------------------------ */
  /* Viewer                                                              */
  /* ------------------------------------------------------------------ */
  async function buildViewer() {
    el.pages.innerHTML = '';
    state.pages = [];
    for (let i = 0; i < state.pdf.numPages; i++) {
      const page = await state.pdf.getPage(i + 1);
      const vp = page.getViewport({ scale: 1 });
      const wrap = h('div', {
        class: 'frps-page',
        'data-index': i,
        role: 'group',
        'aria-label': `Page ${i + 1} of ${state.pdf.numPages}`,
      });
      const canvas = h('canvas', { class: 'frps-page__canvas', 'aria-hidden': 'true' });
      const layer = h('div', { class: 'frps-page__layer' });
      const label = h('span', { class: 'frps-page__num', 'aria-hidden': 'true', text: `${i + 1}` });
      wrap.append(canvas, layer, label);
      el.pages.append(wrap);
      state.pages.push({ index: i, page, vw: vp.width, vh: vp.height, wrap, canvas, layer, renderedScale: 0, task: null });
    }
    el.pageTotal.textContent = state.pdf.numPages;
    el.pageInput.max = state.pdf.numPages;
    layoutPages();
    observePages();
  }

  function baseScale() {
    const avail = Math.max(240, el.viewer.clientWidth - (window.innerWidth < 640 ? 24 : 64));
    const maxVw = Math.max(...state.pages.map((p) => p.vw));
    return Math.min(avail / maxVw, 1.75);
  }

  function layoutPages() {
    const s = baseScale() * state.zoom;
    for (const p of state.pages) {
      p.cssW = Math.round(p.vw * s);
      p.cssH = Math.round(p.vh * s);
      p.wrap.style.width = p.cssW + 'px';
      p.wrap.style.height = p.cssH + 'px';
      p.wrap.style.setProperty('--frps-ph', p.cssH + 'px');
    }
    el.zoomLabel.textContent = Math.round(state.zoom * 100) + '%';
    queueRender();
  }

  let renderQueued = false;
  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      renderVisible();
    });
  }

  function renderVisible() {
    const vr = el.viewer.getBoundingClientRect();
    const margin = vr.height * 1.5;
    for (const p of state.pages) {
      const r = p.wrap.getBoundingClientRect();
      const near = r.bottom > vr.top - margin && r.top < vr.bottom + margin;
      const far = r.bottom < vr.top - margin * 4 || r.top > vr.bottom + margin * 4;
      if (near) renderPage(p);
      else if (far && p.renderedScale) unrenderPage(p);
    }
  }

  function renderPage(p) {
    const ratio = dpr();
    // Cap canvas size for memory safety on phones (~16 MP).
    let scale = (p.cssW / p.vw) * ratio;
    const maxPixels = 16_000_000;
    if (p.vw * p.vh * scale * scale > maxPixels) scale = Math.sqrt(maxPixels / (p.vw * p.vh));
    if (Math.abs(p.renderedScale - scale) < 0.01 || p.task) return;
    const vp = p.page.getViewport({ scale });
    const off = document.createElement('canvas');
    off.width = Math.floor(vp.width);
    off.height = Math.floor(vp.height);
    const ctx = off.getContext('2d', { alpha: false });
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, off.width, off.height);
    p.task = p.page.render({ canvasContext: ctx, viewport: vp, canvas: off });
    p.wrap.classList.add('is-loading');
    p.task.promise
      .then(() => {
        p.canvas.width = off.width;
        p.canvas.height = off.height;
        p.canvas.getContext('2d').drawImage(off, 0, 0);
        p.renderedScale = scale;
        p.wrap.classList.remove('is-loading');
      })
      .catch(() => {})
      .finally(() => {
        p.task = null;
        // If zoom changed while rendering, render again at the new size.
        queueRender();
      });
  }

  function unrenderPage(p) {
    if (p.task) {
      p.task.cancel();
      p.task = null;
    }
    p.canvas.width = 0;
    p.canvas.height = 0;
    p.renderedScale = 0;
  }

  function observePages() {
    if (state.observer) state.observer.disconnect();
    const ratios = new Map();
    state.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) ratios.set(Number(e.target.dataset.index), e.intersectionRatio);
        let best = state.current;
        let bestRatio = -1;
        for (const [i, r] of ratios) if (r > bestRatio) (bestRatio = r), (best = i);
        if (best !== state.current) {
          state.current = best;
          updatePageNav();
        }
      },
      { root: el.viewer, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    state.pages.forEach((p) => state.observer.observe(p.wrap));
  }

  function goToPage(i, smooth = true) {
    i = clamp(i, 0, state.pages.length - 1);
    const p = state.pages[i];
    el.viewer.scrollTo({ top: p.wrap.offsetTop - 16, behavior: smooth ? 'smooth' : 'auto' });
    state.current = i;
    updatePageNav();
  }

  function updatePageNav() {
    if (document.activeElement !== el.pageInput) el.pageInput.value = state.current + 1;
    el.prev.disabled = state.current <= 0;
    el.next.disabled = state.current >= state.pages.length - 1;
  }

  function setZoom(z) {
    const prev = state.zoom;
    state.zoom = clamp(Math.round(z * 100) / 100, 0.5, 3);
    if (prev === state.zoom) return;
    // Keep the same relative scroll position.
    const frac = el.viewer.scrollTop / Math.max(1, el.viewer.scrollHeight);
    layoutPages();
    el.viewer.scrollTop = frac * el.viewer.scrollHeight;
    el.zoomOut.disabled = state.zoom <= 0.5;
    el.zoomIn.disabled = state.zoom >= 3;
  }

  /* ------------------------------------------------------------------ */
  /* Placed signatures                                                   */
  /* ------------------------------------------------------------------ */
  function snapshot() {
    return state.items.map((it) => ({ ...it }));
  }
  function pushHistory() {
    state.undo.push(snapshot());
    if (state.undo.length > 100) state.undo.shift();
    state.redo = [];
    state.dirty = true;
  }
  function undo() {
    if (!state.undo.length) return;
    state.redo.push(snapshot());
    state.items = state.undo.pop();
    if (!state.items.some((i) => i.id === state.selected)) state.selected = null;
    renderItems();
    announce('Undone.');
  }
  function redo() {
    if (!state.redo.length) return;
    state.undo.push(snapshot());
    state.items = state.redo.pop();
    renderItems();
    announce('Redone.');
  }

  function visibleCenterOf(p) {
    const vr = el.viewer.getBoundingClientRect();
    const r = p.wrap.getBoundingClientRect();
    const top = Math.max(r.top, vr.top, 0);
    const bottom = Math.min(r.bottom, vr.bottom, window.innerHeight - 90);
    const cy = bottom > top ? (top + bottom) / 2 : r.top + r.height / 2;
    return { fx: 0.5, fy: clamp((cy - r.top) / r.height, 0, 1) };
  }

  function placeSignature(sigId, pageIndex = state.current, at) {
    const sig = state.sigs.get(sigId);
    const p = state.pages[pageIndex];
    if (!sig || !p) return;
    // Default: ~6.5% of page height tall, max 40% of page width wide.
    let hFrac = 0.065;
    let wFrac = (hFrac * p.vh * sig.aspect) / p.vw;
    if (wFrac > 0.4) {
      wFrac = 0.4;
      hFrac = (wFrac * p.vw) / sig.aspect / p.vh;
    }
    const c = at || visibleCenterOf(p);
    const item = {
      id: uid(),
      page: pageIndex,
      sigId,
      w: wFrac,
      h: hFrac,
      x: clamp(c.fx - wFrac / 2, 0, 1 - wFrac),
      y: clamp(c.fy - hFrac / 2, 0, 1 - hFrac),
    };
    pushHistory();
    state.items.push(item);
    state.selected = item.id;
    renderItems();
    const node = root.querySelector(`[data-item="${item.id}"]`);
    node && node.focus({ preventScroll: true });
    announce(`Signature added to page ${pageIndex + 1}. Drag to move it, use the corner to resize.`);
  }

  /* ---------------- Text boxes + check marks ---------------- */
  const TEXT_FONT = 'Helvetica, Arial, sans-serif';

  function getCheckSig() {
    for (const s of state.sigs.values()) if (s.kind === 'check') return s;
    const c = document.createElement('canvas');
    c.width = 200;
    c.height = 200;
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 26;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(30, 108);
    ctx.lineTo(80, 158);
    ctx.lineTo(172, 46);
    ctx.stroke();
    const url = c.toDataURL('image/png');
    const bin = atob(url.split(',')[1]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const sig = { id: uid(), url, bytes, aspect: 1, kind: 'check' };
    state.sigs.set(sig.id, sig);
    return sig;
  }

  function placeCheck(pageIndex = state.current, at) {
    const p = state.pages[pageIndex];
    if (!p) return;
    const sig = getCheckSig();
    const hFrac = 0.022;
    const wFrac = (hFrac * p.vh) / p.vw;
    const c = at || visibleCenterOf(p);
    const item = {
      id: uid(),
      type: 'check',
      page: pageIndex,
      sigId: sig.id,
      w: wFrac,
      h: hFrac,
      x: clamp(c.fx - wFrac / 2, 0, 1 - wFrac),
      y: clamp(c.fy - hFrac / 2, 0, 1 - hFrac),
    };
    pushHistory();
    state.items.push(item);
    state.selected = item.id;
    renderItems();
    const node = root.querySelector('[data-item="' + item.id + '"]');
    node && node.focus({ preventScroll: true });
    announce('Check mark added. Drag to move it, use the corner to resize, or the bin icon to delete it.');
  }

  function placeText(pageIndex = state.current, at) {
    const p = state.pages[pageIndex];
    if (!p) return;
    const fs = clamp(12 / p.vh, 0.008, 0.05); // about 12pt
    const c = at || { fx: 0.1, fy: visibleCenterOf(p).fy };
    const item = {
      id: uid(),
      type: 'text',
      page: pageIndex,
      text: '',
      fs,
      color: '#111111',
      fresh: true,
      w: 0.05,
      h: fs * 1.45,
      x: clamp(c.fx, 0, 0.95),
      y: clamp(c.fy - fs * 0.725, 0, 1 - fs * 1.45),
    };
    pushHistory();
    state.items.push(item);
    state.selected = item.id;
    renderItems();
    editText(item.id);
    announce('Type your text, then click outside the box when you are done.');
  }

  function measureText(it, node) {
    const p = state.pages[it.page];
    if (!p || !p.cssW || !node) return;
    it.w = Math.min(1, node.offsetWidth / p.cssW);
    it.h = Math.min(1, node.offsetHeight / p.cssH);
    it.x = clamp(it.x, 0, Math.max(0, 1 - it.w));
    it.y = clamp(it.y, 0, Math.max(0, 1 - it.h));
    node.style.left = it.x * 100 + '%';
    node.style.top = it.y * 100 + '%';
  }

  function readText(t) {
    return (t.innerText || '').replace(/\u00a0/g, ' ').replace(/\n$/, '');
  }

  function editText(id) {
    const it = state.items.find((i) => i.id === id);
    const node = root.querySelector('[data-item="' + id + '"]');
    if (!it || !node || it.type !== 'text' || node.classList.contains('is-editing')) return;
    const t = node.querySelector('.frps-item__text');
    const before = snapshot();
    const original = it.text;
    select(id);
    node.classList.add('is-editing');
    t.contentEditable = 'plaintext-only';
    if (t.contentEditable !== 'plaintext-only') t.contentEditable = 'true';
    t.spellcheck = false;
    t.focus({ preventScroll: true });
    try {
      const range = document.createRange();
      range.selectNodeContents(t);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch {}
    const onInput = () => {
      it.text = readText(t);
      measureText(it, node);
    };
    const onKey = (e) => {
      e.stopPropagation();
      if (e.key === 'Escape' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault();
        t.blur();
      }
    };
    const onPaste = (e) => {
      e.preventDefault();
      const txt = (e.clipboardData && e.clipboardData.getData('text/plain')) || '';
      document.execCommand('insertText', false, txt);
    };
    const stop = (e) => e.stopPropagation();
    const finish = () => {
      t.removeEventListener('input', onInput);
      t.removeEventListener('keydown', onKey);
      t.removeEventListener('paste', onPaste);
      t.removeEventListener('pointerdown', stop);
      t.removeAttribute('contenteditable');
      node.classList.remove('is-editing');
      it.text = readText(t);
      const fresh = it.fresh;
      delete it.fresh;
      if (!it.text.trim()) {
        const idx = state.items.indexOf(it);
        if (idx >= 0) state.items.splice(idx, 1);
        if (fresh) state.undo.pop();
        else {
          state.undo.push(before);
          state.redo = [];
        }
        if (state.selected === it.id) state.selected = null;
        renderItems();
        return;
      }
      if (!fresh && it.text !== original) {
        state.undo.push(before);
        if (state.undo.length > 100) state.undo.shift();
        state.redo = [];
      }
      state.dirty = true;
      measureText(it, node);
      updateUI();
    };
    t.addEventListener('input', onInput);
    t.addEventListener('keydown', onKey);
    t.addEventListener('paste', onPaste);
    t.addEventListener('pointerdown', stop);
    t.addEventListener('blur', finish, { once: true });
  }

  function setPlacing(mode) {
    state.placing = mode || null;
    $$('[data-frps-place]').forEach((b) =>
      b.setAttribute('aria-pressed', b.dataset.frpsPlace === state.placing ? 'true' : 'false')
    );
    el.viewer.classList.toggle('is-placing', !!state.placing);
    if (state.placing)
      announce(state.placing === 'text' ? 'Click on the page where you want to type.' : 'Click on the box where you want the check mark.');
  }

  async function textToPng(it, vp) {
    const k = 4;
    const fontPx = it.fs * vp.height * k;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(it.w * vp.width * k));
    c.height = Math.max(1, Math.round(it.h * vp.height * k));
    const ctx = c.getContext('2d');
    ctx.font = fontPx + 'px ' + TEXT_FONT;
    ctx.fillStyle = it.color || '#111111';
    ctx.textBaseline = 'middle';
    const lh = fontPx * 1.25;
    it.text.split('\n').forEach((line, i) => ctx.fillText(line, fontPx * 0.2, fontPx * 0.1 + lh * (i + 0.5)));
    const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
    return new Uint8Array(await blob.arrayBuffer());
  }

  function removeItem(id) {
    const idx = state.items.findIndex((i) => i.id === id);
    if (idx < 0) return;
    const it0 = state.items[idx];
    pushHistory();
    state.items.splice(idx, 1);
    if (state.selected === id) state.selected = null;
    renderItems();
    el.viewer.focus({ preventScroll: true });
    announce((it0.type === 'text' ? 'Text' : it0.type === 'check' ? 'Check mark' : 'Signature') + ' deleted. Press Undo to bring it back.');
  }

  function duplicateItem(id) {
    const it = state.items.find((i) => i.id === id);
    if (!it) return;
    pushHistory();
    const copy = { ...it, id: uid(), x: clamp(it.x + 0.03, 0, 1 - it.w), y: clamp(it.y + 0.03, 0, 1 - it.h) };
    state.items.push(copy);
    state.selected = copy.id;
    renderItems();
    announce('Duplicated.');
  }

  function replaceItemSig(id, sigId) {
    const it = state.items.find((i) => i.id === id);
    const sig = state.sigs.get(sigId);
    if (!it || !sig) return;
    const p = state.pages[it.page];
    pushHistory();
    // Keep the same height and centre; adapt width to the new signature's shape.
    const cx = it.x + it.w / 2;
    let w = (it.h * p.vh * sig.aspect) / p.vw;
    let hh = it.h;
    if (w > 0.95) {
      w = 0.95;
      hh = (w * p.vw) / sig.aspect / p.vh;
    }
    Object.assign(it, { sigId, w, h: hh, x: clamp(cx - w / 2, 0, 1 - w), y: clamp(it.y, 0, 1 - hh) });
    state.selected = id;
    renderItems();
    announce('Signature replaced.');
  }

  function renderItems() {
    for (const p of state.pages) p.layer.innerHTML = '';
    for (const it of state.items) {
      const p = state.pages[it.page];
      const isText = it.type === 'text';
      const sig = isText ? null : state.sigs.get(it.sigId);
      if (!p || (!isText && !sig)) continue;
      const selected = it.id === state.selected;
      const node = h(
        'div',
        {
          class: 'frps-item' + (isText ? ' frps-item--text' : '') + (it.type === 'check' ? ' frps-item--check' : '') + (selected ? ' is-selected' : ''),
          'data-item': it.id,
          tabindex: '0',
          role: 'button',
          'aria-label': `${isText ? 'Text box' : it.type === 'check' ? 'Check mark' : 'Signature'} on page ${it.page + 1}. Use arrow keys to move, Shift plus arrow keys to resize, Delete to remove.`,
          'aria-pressed': selected ? 'true' : 'false',
        },
        isText ? h('div', { class: 'frps-item__text', 'data-placeholder': 'Type here' }) : h('img', { src: sig.url, alt: '', draggable: 'false' })
      );
      node.style.left = it.x * 100 + '%';
      node.style.top = it.y * 100 + '%';
      node.style.width = it.w * 100 + '%';
      node.style.height = it.h * 100 + '%';
      if (isText) {
        node.style.setProperty('--fs', it.fs);
        node.style.color = it.color || '#111111';
        node.firstChild.textContent = it.text;
      }

      const handle = h('span', { class: 'frps-item__handle', 'data-handle': '1', 'aria-hidden': 'true' });
      handle.innerHTML = icon('resize');
      const bar = h('div', { class: 'frps-item__bar' });
      const mk = (name, label, fn) => {
        const b = h('button', { type: 'button', class: 'frps-item__btn', 'aria-label': label, title: label });
        b.innerHTML = icon(name);
        b.addEventListener('pointerdown', (e) => e.stopPropagation());
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          fn();
        });
        return b;
      };
      if (isText)
        bar.append(
          mk('pencil', 'Edit text', () => editText(it.id)),
          mk('copy', 'Duplicate text', () => duplicateItem(it.id)),
          mk('trash', 'Delete text', () => removeItem(it.id))
        );
      else if (it.type === 'check')
        bar.append(
          mk('copy', 'Duplicate check mark', () => duplicateItem(it.id)),
          mk('trash', 'Delete check mark', () => removeItem(it.id))
        );
      else bar.append(
        mk('swap', 'Replace signature', () => openDialog({ replaceId: it.id })),
        mk('copy', 'Duplicate signature', () => duplicateItem(it.id)),
        mk('trash', 'Remove signature', () => removeItem(it.id))
      );
      node.append(handle, bar);
      bindItem(node, it);
      p.layer.append(node);
      if (isText) measureText(it, node);
    }
    updateUI();
  }

  function select(id) {
    if (state.selected === id) return;
    state.selected = id;
    $$('.frps-item').forEach((n) => {
      const on = n.dataset.item === id;
      n.classList.toggle('is-selected', on);
      n.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function bindItem(node, it) {
    node.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button > 0) return;
      if (e.target.closest('[contenteditable]')) return;
      const wasSelected = state.selected === it.id;
      e.preventDefault();
      e.stopPropagation();
      select(it.id);
      node.focus({ preventScroll: true });
      const p = state.pages[it.page];
      const rect = p.wrap.getBoundingClientRect();
      const resizing = !!e.target.closest('[data-handle]');
      const start = { x: e.clientX, y: e.clientY, it: { ...it } };
      const sig = state.sigs.get(it.sigId);
      const aspectFrac = sig ? (p.vw / p.vh) / sig.aspect : 1; // h = w * aspectFrac
      let moved = false;
      let before = null;
      node.setPointerCapture(e.pointerId);
      node.classList.add(resizing ? 'is-resizing' : 'is-dragging');

      const onMove = (ev) => {
        const dx = (ev.clientX - start.x) / rect.width;
        const dy = (ev.clientY - start.y) / rect.height;
        if (!moved && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 3) return;
        if (!moved) {
          moved = true;
          before = snapshot();
        }
        if (resizing && it.type === 'text') {
          const f = clamp((start.it.w + dx) / start.it.w, 0.2, 12);
          it.fs = clamp(start.it.fs * f, 0.006, 0.25);
          node.style.setProperty('--fs', it.fs);
          measureText(it, node);
        } else if (resizing) {
          let w = clamp(start.it.w + dx, it.type === 'check' ? 0.008 : 0.03, 1 - start.it.x);
          let hh = w * aspectFrac;
          if (start.it.y + hh > 1) {
            hh = 1 - start.it.y;
            w = hh / aspectFrac;
          }
          it.w = w;
          it.h = hh;
          node.style.width = w * 100 + '%';
          node.style.height = hh * 100 + '%';
        } else {
          it.x = clamp(start.it.x + dx, 0, 1 - it.w);
          it.y = clamp(start.it.y + dy, 0, 1 - it.h);
          node.style.left = it.x * 100 + '%';
          node.style.top = it.y * 100 + '%';
        }
      };
      const onUp = () => {
        node.removeEventListener('pointermove', onMove);
        node.removeEventListener('pointerup', onUp);
        node.removeEventListener('pointercancel', onUp);
        node.classList.remove('is-resizing', 'is-dragging');
        if (moved) {
          state.undo.push(before);
          state.redo = [];
          state.dirty = true;
          updateUI();
        } else if (it.type === 'text' && wasSelected && !resizing) editText(it.id);
      };
      node.addEventListener('pointermove', onMove);
      node.addEventListener('pointerup', onUp);
      node.addEventListener('pointercancel', onUp);
    });

    node.addEventListener('keydown', (e) => {
      const step = e.altKey ? 0.002 : 0.01;
      const p = state.pages[it.page];
      const sig = state.sigs.get(it.sigId);
      const aspectFrac = sig ? (p.vw / p.vh) / sig.aspect : 1;
      let handled = true;
      if (it.type === 'text' && e.key === 'Enter') editText(it.id);
      else if (e.key === 'Delete' || e.key === 'Backspace') removeItem(it.id);
      else if (e.key === 'Escape') {
        select(null);
        node.blur();
      } else if (e.key.startsWith('Arrow')) {
        pushHistory();
        if (e.shiftKey && it.type === 'text') {
          const g2 = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1.06 : 1 / 1.06;
          it.fs = clamp(it.fs * g2, 0.006, 0.25);
          node.style.setProperty('--fs', it.fs);
          measureText(it, node);
        } else if (e.shiftKey) {
          const grow = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : -1;
          let w = clamp(it.w + grow * step * 2, 0.03, 1 - it.x);
          let hh = w * aspectFrac;
          if (it.y + hh > 1) {
            hh = 1 - it.y;
            w = hh / aspectFrac;
          }
          it.w = w;
          it.h = hh;
        } else {
          if (e.key === 'ArrowLeft') it.x -= step;
          if (e.key === 'ArrowRight') it.x += step;
          if (e.key === 'ArrowUp') it.y -= step;
          if (e.key === 'ArrowDown') it.y += step;
          it.x = clamp(it.x, 0, 1 - it.w);
          it.y = clamp(it.y, 0, 1 - it.h);
        }
        node.style.left = it.x * 100 + '%';
        node.style.top = it.y * 100 + '%';
        node.style.width = it.w * 100 + '%';
        node.style.height = it.h * 100 + '%';
        updateUI();
      } else handled = false;
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
    node.addEventListener('focus', () => select(it.id));
  }

  /* ------------------------------------------------------------------ */
  /* Signature tray (signatures created this session)                    */
  /* ------------------------------------------------------------------ */
  function renderTray() {
    el.tray.innerHTML = '';
    el.trayEmpty.hidden = [...state.sigs.values()].some((s) => s.kind !== 'check');
    for (const sig of state.sigs.values()) {
      if (sig.kind === 'check') continue;
      const b = h(
        'button',
        {
          type: 'button',
          class: 'frps-tray__sig',
          'aria-label': 'Place this signature on the current page',
          title: 'Place on current page',
          onclick: () => placeSignature(sig.id),
        },
        h('img', { src: sig.url, alt: '' })
      );
      el.tray.append(b);
    }
  }

  /* ------------------------------------------------------------------ */
  /* UI state                                                            */
  /* ------------------------------------------------------------------ */
  function updateUI() {
    el.undo.disabled = !state.undo.length;
    el.redo.disabled = !state.redo.length;
    const n = state.items.length;
    el.download.forEach((b) => {
      b.disabled = n === 0;
      b.setAttribute('aria-disabled', n === 0 ? 'true' : 'false');
    });
    el.placedCount.textContent = n === 0 ? 'Nothing placed yet' : `${n} item${n > 1 ? 's' : ''} placed`;
    updatePageNav();
  }

  /* ------------------------------------------------------------------ */
  /* Signature dialog: draw / type / upload                              */
  /* ------------------------------------------------------------------ */
  const dlg = {
    mode: 'draw',
    replaceId: null,
    color: 'black',
    font: 0,
    strokes: [],
    current: null,
    uploadCanvas: null,
    uploadSource: null,
  };
  const pad = $('[data-frps-pad]', el.dialog);
  const padCtx = pad.getContext('2d');
  const typeInput = $('[data-frps-type-input]', el.dialog);
  const fontList = $('[data-frps-fonts]', el.dialog);
  const uploadInput = $('[data-frps-sig-file]', el.dialog);
  const uploadPreview = $('[data-frps-upload-preview]', el.dialog);
  const uploadError = $('[data-frps-upload-error]', el.dialog);
  const removeBg = $('[data-frps-remove-bg]', el.dialog);
  const useBtn = $('[data-frps-use]', el.dialog);
  const dlgTitle = $('[data-frps-dialog-title]', el.dialog);

  function openDialog({ replaceId = null } = {}) {
    dlg.replaceId = replaceId;
    dlgTitle.textContent = replaceId ? 'Replace signature' : 'Create your signature';
    useBtn.textContent = replaceId ? 'Replace signature' : 'Add to document';
    if (typeof el.dialog.showModal === 'function') el.dialog.showModal();
    else el.dialog.setAttribute('open', '');
    document.documentElement.classList.add('frps-lock');
    setMode(dlg.mode);
    requestAnimationFrame(sizePad);
  }
  function closeDialog() {
    if (typeof el.dialog.close === 'function') el.dialog.close();
    else el.dialog.removeAttribute('open');
  }
  el.dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('frps-lock');
  });
  el.dialog.addEventListener('click', (e) => {
    // Click on the backdrop closes the dialog.
    if (e.target === el.dialog) closeDialog();
  });
  $$('[data-frps-close]', el.dialog).forEach((b) => b.addEventListener('click', closeDialog));

  function setMode(mode) {
    dlg.mode = mode;
    $$('[data-frps-tab]', el.dialog).forEach((t) => {
      const on = t.dataset.frpsTab === mode;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    $$('[data-frps-panel]', el.dialog).forEach((p) => (p.hidden = p.dataset.frpsPanel !== mode));
    $('[data-frps-colors]', el.dialog).hidden = mode === 'upload';
    if (mode === 'type') {
      buildFontList();
      loadSignatureFonts().then(buildFontList);
      setTimeout(() => typeInput.focus(), 30);
    }
    if (mode === 'draw') requestAnimationFrame(sizePad);
    validateDialog();
  }

  const tabs = $$('[data-frps-tab]', el.dialog);
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => setMode(t.dataset.frpsTab));
    t.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      n.focus();
      setMode(n.dataset.frpsTab);
    });
  });

  $$('[data-frps-color]', el.dialog).forEach((b) =>
    b.addEventListener('click', () => {
      dlg.color = b.dataset.frpsColor;
      $$('[data-frps-color]', el.dialog).forEach((x) => x.setAttribute('aria-checked', x === b ? 'true' : 'false'));
      redrawPad();
      buildFontList();
    })
  );

  /* --- Draw --- */
  function sizePad() {
    if (dlg.mode !== 'draw' || !pad.isConnected) return;
    const r = pad.getBoundingClientRect();
    if (!r.width) return;
    const ratio = dpr();
    pad.width = Math.round(r.width * ratio);
    pad.height = Math.round(r.height * ratio);
    padCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
    redrawPad();
  }
  window.addEventListener('resize', () => el.dialog.open && sizePad());

  function strokePath(ctx, pts, width, scale = 1) {
    if (!pts.length) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = width * scale;
    ctx.beginPath();
    if (pts.length === 1) {
      ctx.arc(pts[0].x * scale, pts[0].y * scale, (width * scale) / 2, 0, Math.PI * 2);
      ctx.fillStyle = ctx.strokeStyle;
      ctx.fill();
      return;
    }
    ctx.moveTo(pts[0].x * scale, pts[0].y * scale);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i].x + pts[i + 1].x) / 2;
      const my = (pts[i].y + pts[i + 1].y) / 2;
      ctx.quadraticCurveTo(pts[i].x * scale, pts[i].y * scale, mx * scale, my * scale);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last.x * scale, last.y * scale);
    ctx.stroke();
  }

  function redrawPad() {
    const r = pad.getBoundingClientRect();
    padCtx.clearRect(0, 0, r.width, r.height);
    padCtx.strokeStyle = INK[dlg.color];
    for (const s of dlg.strokes) strokePath(padCtx, s.pts, s.w);
    if (dlg.current) strokePath(padCtx, dlg.current.pts, dlg.current.w);
    $('[data-frps-pad-hint]', el.dialog).hidden = dlg.strokes.length > 0 || !!dlg.current;
  }

  function padPoint(e) {
    const r = pad.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  pad.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pad.setPointerCapture(e.pointerId);
    const r = pad.getBoundingClientRect();
    dlg.current = { pts: [padPoint(e)], w: clamp(r.width / 160, 2.2, 3.6) };
    redrawPad();
  });
  pad.addEventListener('pointermove', (e) => {
    if (!dlg.current) return;
    const evs = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
    for (const ev of evs.length ? evs : [e]) dlg.current.pts.push(padPoint(ev));
    redrawPad();
  });
  const endStroke = () => {
    if (!dlg.current) return;
    dlg.strokes.push(dlg.current);
    dlg.current = null;
    redrawPad();
    validateDialog();
  };
  pad.addEventListener('pointerup', endStroke);
  pad.addEventListener('pointercancel', endStroke);
  $('[data-frps-pad-clear]', el.dialog).addEventListener('click', () => {
    dlg.strokes = [];
    redrawPad();
    validateDialog();
  });
  $('[data-frps-pad-undo]', el.dialog).addEventListener('click', () => {
    dlg.strokes.pop();
    redrawPad();
    validateDialog();
  });

  function drawnToCanvas() {
    const r = pad.getBoundingClientRect();
    const scale = 3;
    const c = document.createElement('canvas');
    c.width = Math.round(r.width * scale);
    c.height = Math.round(r.height * scale);
    const ctx = c.getContext('2d');
    ctx.strokeStyle = INK[dlg.color];
    for (const s of dlg.strokes) strokePath(ctx, s.pts, s.w, scale);
    return c;
  }

  /* --- Type --- */
  function buildFontList() {
    const name = typeInput.value.trim() || 'Your Name';
    fontList.innerHTML = '';
    SIG_FONTS.forEach((f, i) => {
      const b = h(
        'button',
        {
          type: 'button',
          role: 'radio',
          class: 'frps-font',
          'aria-checked': dlg.font === i ? 'true' : 'false',
          'aria-label': `${f.label} style`,
          onclick: () => {
            dlg.font = i;
            buildFontList();
            validateDialog();
          },
        },
        h('span', { class: 'frps-font__sample', text: name })
      );
      const s = b.firstChild;
      s.style.fontFamily = `"${f.family}", "Brush Script MT", "Segoe Script", cursive`;
      s.style.fontWeight = f.weight;
      s.style.color = INK[dlg.color];
      fontList.append(b);
    });
  }
  typeInput.addEventListener('input', () => {
    buildFontList();
    validateDialog();
  });
  typeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!useBtn.disabled) useBtn.click();
    }
  });

  async function typedToCanvas() {
    await loadSignatureFonts();
    const f = SIG_FONTS[dlg.font];
    const text = typeInput.value.trim();
    const size = 160;
    const font = `${f.weight} ${size}px "${f.family}", "Brush Script MT", "Segoe Script", cursive`;
    try {
      await document.fonts.load(font, text);
    } catch {}
    const m = document.createElement('canvas').getContext('2d');
    m.font = font;
    const w = Math.ceil(m.measureText(text).width) + size;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = Math.round(size * 2.2);
    const ctx = c.getContext('2d');
    ctx.font = font;
    ctx.fillStyle = INK[dlg.color];
    ctx.textBaseline = 'middle';
    ctx.fillText(text, size / 2, c.height / 2);
    return c;
  }

  /* --- Upload --- */
  uploadInput.addEventListener('change', async () => {
    const f = uploadInput.files && uploadInput.files[0];
    uploadInput.value = '';
    uploadError.hidden = true;
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) {
      uploadError.textContent = 'Please choose a PNG, JPG or WebP image.';
      uploadError.hidden = false;
      return;
    }
    if (f.size > cfg.maxSigImageMB * 1048576) {
      uploadError.textContent = `That image is larger than ${cfg.maxSigImageMB} MB. Please choose a smaller image.`;
      uploadError.hidden = false;
      return;
    }
    const url = URL.createObjectURL(f);
    try {
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = url;
      });
      const max = 1600;
      const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.naturalWidth * k));
      c.height = Math.max(1, Math.round(img.naturalHeight * k));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      dlg.uploadSource = c;
      refreshUploadPreview();
    } catch {
      uploadError.textContent = 'We couldn’t read that image. Please try a different file.';
      uploadError.hidden = false;
    } finally {
      URL.revokeObjectURL(url);
    }
  });
  removeBg.addEventListener('change', refreshUploadPreview);

  function refreshUploadPreview() {
    if (!dlg.uploadSource) return;
    const src = dlg.uploadSource;
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    const ctx = c.getContext('2d');
    ctx.drawImage(src, 0, 0);
    if (removeBg.checked) {
      const d = ctx.getImageData(0, 0, c.width, c.height);
      const px = d.data;
      for (let i = 0; i < px.length; i += 4) {
        const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        if (lum > 235) px[i + 3] = 0;
        else if (lum > 190) px[i + 3] = Math.round(px[i + 3] * ((235 - lum) / 45));
      }
      ctx.putImageData(d, 0, 0);
    }
    dlg.uploadCanvas = c;
    uploadPreview.innerHTML = '';
    const img = h('img', { src: c.toDataURL('image/png'), alt: 'Preview of your uploaded signature' });
    uploadPreview.append(img);
    uploadPreview.hidden = false;
    validateDialog();
  }

  /* --- Shared --- */
  function trimCanvas(c, padPx = 6) {
    const ctx = c.getContext('2d');
    const { width: w, height: hh } = c;
    const data = ctx.getImageData(0, 0, w, hh).data;
    let x0 = w,
      y0 = hh,
      x1 = -1,
      y1 = -1;
    for (let y = 0; y < hh; y++) {
      for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > 8) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return null;
    x0 = Math.max(0, x0 - padPx);
    y0 = Math.max(0, y0 - padPx);
    x1 = Math.min(w - 1, x1 + padPx);
    y1 = Math.min(hh - 1, y1 + padPx);
    const out = document.createElement('canvas');
    out.width = x1 - x0 + 1;
    out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  function validateDialog() {
    let ok = false;
    if (dlg.mode === 'draw') ok = dlg.strokes.length > 0;
    if (dlg.mode === 'type') ok = typeInput.value.trim().length > 0;
    if (dlg.mode === 'upload') ok = !!dlg.uploadCanvas;
    useBtn.disabled = !ok;
  }

  useBtn.addEventListener('click', async () => {
    useBtn.disabled = true;
    try {
      let c;
      if (dlg.mode === 'draw') c = drawnToCanvas();
      else if (dlg.mode === 'type') c = await typedToCanvas();
      else c = dlg.uploadCanvas;
      const trimmed = c && trimCanvas(c);
      if (!trimmed) {
        announce(
          dlg.mode === 'upload' ? 'That image looks blank. Try turning off “Remove white background”.' : 'Your signature is empty.',
          'error'
        );
        return;
      }
      const blob = await new Promise((res) => trimmed.toBlob(res, 'image/png'));
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const sig = {
        id: uid(),
        url: URL.createObjectURL(blob),
        bytes,
        aspect: trimmed.width / trimmed.height,
      };
      state.sigs.set(sig.id, sig);
      renderTray();
      const replaceId = dlg.replaceId;
      closeDialog();
      if (replaceId) replaceItemSig(replaceId, sig.id);
      else placeSignature(sig.id);
      // Reset drawing for next time.
      dlg.strokes = [];
    } finally {
      validateDialog();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Export                                                              */
  /* ------------------------------------------------------------------ */
  async function download() {
    if (!state.items.length) {
      announce('Add at least one signature before downloading.', 'error');
      return;
    }
    el.download.forEach((b) => b.classList.add('is-busy'));
    announce('Preparing your signed PDF…');
    try {
      const PDFLib = await loadPdfLib();
      const doc = await PDFLib.PDFDocument.load(state.bytes, { updateMetadata: false });
      const pages = doc.getPages();
      const embedded = new Map();
      for (const it of state.items) {
        const page = pages[it.page];
        if (!page) continue;
        const vp = state.pages[it.page].page.getViewport({ scale: 1 });
        let img;
        if (it.type === 'text') {
          if (!it.text || !it.text.trim()) continue;
          img = await doc.embedPng(await textToPng(it, vp));
        } else {
          const sig = state.sigs.get(it.sigId);
          if (!sig) continue;
          if (!embedded.has(sig.id)) embedded.set(sig.id, await doc.embedPng(sig.bytes));
          img = embedded.get(sig.id);
        }
        const dx = it.x * vp.width;
        const dy = it.y * vp.height;
        const dw = it.w * vp.width;
        const dh = it.h * vp.height;
        // Map the on-screen box (top-left origin, rotated view) into PDF user space.
        const [x0, y0] = vp.convertToPdfPoint(dx, dy + dh);
        const [x1, y1] = vp.convertToPdfPoint(dx + dw, dy + dh);
        const angle = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
        page.drawImage(img, { x: x0, y: y0, width: dw, height: dh, rotate: PDFLib.degrees(angle) });
      }
      const out = await doc.save();
      const blob = new Blob([out], { type: 'application/pdf' });
      const name = state.file.name.replace(/\.pdf$/i, '') + '-signed.pdf';
      // Inside the Claude preview sandbox, use its save prompt; on the store, a normal download.
      const host = window.claude && typeof window.claude.use === 'function' ? await window.claude.use('downloads').catch(() => null) : null;
      if (host) {
        try {
          await host.save({ filename: name, data: blob });
        } catch (e) {
          if (e && e.code === 'declined') return announce('Download cancelled.');
          throw e;
        }
      } else {
        const url = URL.createObjectURL(blob);
        const a = h('a', { href: url, download: name, style: 'display:none' });
        document.body.append(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      state.dirty = false;
      announce(`Done! “${name}” has been downloaded.`, 'success');
    } catch (err) {
      console.error('[PDF Sign]', err);
      announce('Sorry, we couldn’t create the signed PDF. Please try again.', 'error');
    } finally {
      el.download.forEach((b) => b.classList.remove('is-busy'));
    }
  }

  function destroyPdf(pdf) {
    try {
      if (!pdf) return;
      if (typeof pdf.destroy === 'function') pdf.destroy();
      else if (pdf.loadingTask) pdf.loadingTask.destroy();
    } catch {}
  }

  /* ------------------------------------------------------------------ */
  /* Reset                                                               */
  /* ------------------------------------------------------------------ */
  function resetAll(silent) {
    if (state.observer) state.observer.disconnect();
    state.pages.forEach(unrenderPage);
    destroyPdf(state.pdf);
    for (const s of state.sigs.values()) URL.revokeObjectURL(s.url);
    Object.assign(state, {
      file: null,
      bytes: null,
      pdf: null,
      pages: [],
      items: [],
      sigs: new Map(),
      selected: null,
      undo: [],
      redo: [],
      zoom: 1,
      current: 0,
      dirty: false,
      observer: null,
    });
    setPlacing(null);
    el.pages.innerHTML = '';
    el.workspace.hidden = true;
    el.upload.hidden = false;
    renderTray();
    if (!silent) {
      announce('Cleared. Your document has been removed from this page.');
      el.drop.focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */
  el.fileInput.addEventListener('change', () => {
    const f = el.fileInput.files && el.fileInput.files[0];
    el.fileInput.value = '';
    handleFile(f);
  });
  el.drop.addEventListener('click', (e) => {
    if (e.target.closest('label, input')) return;
    el.fileInput.click();
  });
  el.drop.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      el.fileInput.click();
    }
  });
  ['pointerenter', 'focusin', 'touchstart'].forEach((ev) => el.drop.addEventListener(ev, warmUp, { once: true, passive: true }));
  ['dragenter', 'dragover'].forEach((ev) =>
    el.drop.addEventListener(ev, (e) => {
      e.preventDefault();
      el.drop.classList.add('is-over');
      warmUp();
    })
  );
  ['dragleave', 'dragend'].forEach((ev) => el.drop.addEventListener(ev, () => el.drop.classList.remove('is-over')));
  el.drop.addEventListener('drop', (e) => {
    e.preventDefault();
    el.drop.classList.remove('is-over');
    const files = e.dataTransfer && e.dataTransfer.files;
    if (files && files.length > 1) announce('Only the first file was opened. Please sign one PDF at a time.');
    handleFile(files && files[0]);
  });
  // Stop the browser from opening a PDF dropped slightly outside the zone.
  ['dragover', 'drop'].forEach((ev) =>
    root.addEventListener(ev, (e) => {
      if (!el.upload.hidden) e.preventDefault();
    })
  );

  el.prev.addEventListener('click', () => goToPage(state.current - 1));
  el.next.addEventListener('click', () => goToPage(state.current + 1));
  el.pageInput.addEventListener('change', () => {
    const n = parseInt(el.pageInput.value, 10);
    if (Number.isFinite(n)) goToPage(n - 1);
    else updatePageNav();
  });
  el.zoomIn.addEventListener('click', () => setZoom(state.zoom + 0.25));
  el.zoomOut.addEventListener('click', () => setZoom(state.zoom - 0.25));
  el.undo.addEventListener('click', undo);
  el.redo.addEventListener('click', redo);
  el.addSig.forEach((b) => b.addEventListener('click', () => openDialog()));
  $$('[data-frps-place]').forEach((b) =>
    b.addEventListener('click', () => setPlacing(state.placing === b.dataset.frpsPlace ? null : b.dataset.frpsPlace))
  );
  el.pages.addEventListener('click', (e) => {
    if (!state.placing || e.target.closest('.frps-item')) return;
    const wrap = e.target.closest('.frps-page');
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const at = { fx: clamp((e.clientX - r.left) / r.width, 0, 1), fy: clamp((e.clientY - r.top) / r.height, 0, 1) };
    const mode = state.placing;
    setPlacing(null);
    if (mode === 'text') placeText(Number(wrap.dataset.index), at);
    else placeCheck(Number(wrap.dataset.index), at);
  });
  el.download.forEach((b) => b.addEventListener('click', download));
  // Two-step confirmation inside the page (no browser pop-ups).
  let restartArmed = null;
  const restartLabel = el.restart.textContent;
  el.restart.addEventListener('click', () => {
    if (state.items.length && state.dirty && !restartArmed) {
      el.restart.textContent = 'Click again to remove your signatures and start over';
      el.restart.classList.add('is-armed');
      restartArmed = setTimeout(() => {
        restartArmed = null;
        el.restart.textContent = restartLabel;
        el.restart.classList.remove('is-armed');
      }, 5000);
      return;
    }
    clearTimeout(restartArmed);
    restartArmed = null;
    el.restart.textContent = restartLabel;
    el.restart.classList.remove('is-armed');
    resetAll();
  });

  el.viewer.addEventListener('scroll', queueRender, { passive: true });
  el.viewer.addEventListener('pointerdown', (e) => {
    if (!e.target.closest('.frps-item')) select(null);
  });

  document.addEventListener('keydown', (e) => {
    if (el.workspace.hidden || el.dialog.open) return;
    if (e.key === 'Escape' && state.placing) {
      setPlacing(null);
      return;
    }
    const t = e.target instanceof Element ? e.target : document.body;
    // Only react when focus is inside the tool, or nowhere in particular (e.g. after a removed button).
    if (t !== document.body && !root.contains(t)) return;
    if (t.closest('input, textarea, select, [contenteditable]')) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    } else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
    } else if (e.target === el.viewer && (e.key === 'PageDown' || e.key === 'PageUp')) {
      e.preventDefault();
      goToPage(state.current + (e.key === 'PageDown' ? 1 : -1));
    }
  });

  let resizeTimer;
  let lastWidth = el.viewer.clientWidth;
  new ResizeObserver(() => {
    if (!state.pages.length) return;
    const w = el.viewer.clientWidth;
    if (Math.abs(w - lastWidth) < 2) return;
    lastWidth = w;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(layoutPages, 120);
  }).observe(el.viewer);

  window.addEventListener('beforeunload', (e) => {
    if (state.dirty && state.items.length) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // Expose a tiny hook for automated testing only (no document data).
  root.frps = { state, placeSignature, placeText, placeCheck, download };
})();
